import Head from 'next/head';
import React, { useMemo } from 'react';
import { cityWithFilters, defaultFilters, nearCitiesFilters, placeFilters, provinceWithFilters, villageFilters } from './breadcrumb/Breadcrumb';
import { digitsEnToFa } from '@persian-tools/persian-tools';
import { getDescription, getText, getTitle, mainQueryLength } from '../reusable/utils/Utils';
import { convertUrlToParamsObject, toEnglish, toPersian } from '../../../helpers/props';
import { RouteParams, tags } from '../types';
import usePlaceName from '../hooks/usePlaceName';
import { SearchDataProps } from '../_types/searchDataType';
import { useIsWhiteLabel } from '@/components/app/providers/whiteLabelProvider/WhiteLabelProvider';
const image = `https://www.shab.ir/images/home/hero/hero.webp`;
export const mainFilters = ['apartment', 'boomgardi', 'cottage', 'swiss-cottage', 'pool', 'indoor_pool', 'outdoor_pool', 'barbecue', 'balcony', 'billiard-pool', 'coastal', 'desert', 'forest', 'mountain', 'mid_term'];
type ISearchSeo = {
  queries: {
    [key: string]: string | string[] | undefined;
  };
  params: RouteParams;
  perPage: number;
  data: SearchDataProps;
  host: string;
};
const SearchSeo = ({
  queries: queryParams,
  params,
  perPage,
  data,
  host
}: ISearchSeo) => {
  const {
    city,
    province,
    url,
    village
  } = params;
  const pathname = url?.split('?')[0] || '';
  const isWhiteLabel = useIsWhiteLabel();
  const defaultMinPrice = data?.min_price?.amount ? digitsEnToFa(data?.min_price?.amount) : '';
  const calculatedPrice = (+toEnglish(defaultMinPrice) / 1000).toString();
  const minPrice = calculatedPrice.length > 3 ? `${toPersian(parseFloat((+calculatedPrice / 1000).toFixed(1)))} میلیون` : `${toPersian(calculatedPrice)} هزار`;
  const href = isWhiteLabel ? `${host}${url}` : `https://www.shab.ir${url}`;
  const pageQuery = convertUrlToParamsObject(href)?.page || 1;
  const urlWithoutPageNext = new URL(href);
  const urlWithoutPagePrev = new URL(href);
  const nextPage = pageQuery ? +pageQuery + 1 : 1;
  const prevPage = +pageQuery - 1;
  const mainTotal = Number(Math.ceil(data.pagination?.total / perPage)) || 0;
  const nextExists = +pageQuery < mainTotal;
  urlWithoutPageNext.searchParams.delete('page');
  urlWithoutPagePrev.searchParams.delete('page');
  urlWithoutPageNext.searchParams.append('page', nextPage.toString());
  if (urlWithoutPageNext.href?.endsWith('search?cottage=1')) {
    urlWithoutPageNext.href.replace('search?cottage=1', 'all-cottage');
    urlWithoutPagePrev.href.replace('search?cottage=1', 'all-cottage');
  }
  if (urlWithoutPageNext.href?.endsWith('search?boomgardi=1')) {
    urlWithoutPageNext.href.replace('search?boomgardi=1', 'all-boomgardi');
    urlWithoutPagePrev.href.replace('search?boomgardi=1', 'all-boomgardi');
  }
  if (+prevPage > 1) {
    urlWithoutPagePrev.searchParams.append('page', prevPage.toString());
  }
  const hrefWithoutExtraFilters = new URL(href);
  Object.keys(convertUrlToParamsObject(href)).map(item => {
    if (!mainFilters?.includes(item)) {
      if (item !== hrefWithoutExtraFilters.href) {
        hrefWithoutExtraFilters.searchParams.delete(item);
      }
    }
  });
  const placeName = usePlaceName(queryParams);

  // finds random number between 250~350 by Math.floor(Math.random() * (max - min + 1) + min) formula
  const randomPrice = () => Math.floor(Math.random() * 101 + 250);
  const title = getTitle({
    queryParams,
    city,
    minPrice: data?.list?.length ? minPrice : `${toPersian(randomPrice())} هزار`,
    province,
    href,
    data,
    placeName,
    village
  });
  const description = getDescription({
    queryParams,
    city,
    province,
    data,
    minPrice: data?.list?.length ? minPrice : `${randomPrice()} هزار`,
    href,
    placeName,
    village
  });
  let breadcrumbs = city || province ? city ? cityWithFilters({
    city,
    href: hrefWithoutExtraFilters.searchParams.size >= 1 ? hrefWithoutExtraFilters.href : '',
    province: data?.region_data?.province,
    queryParams,
    data
  }) : provinceWithFilters({
    queryParams,
    province,
    href: hrefWithoutExtraFilters.href?.includes('?') && hrefWithoutExtraFilters.href?.includes('=') ? hrefWithoutExtraFilters.href : '',
    data
  }) : defaultFilters({
    href: queryParams.parent_id ? `${href}/search?parent_id=${queryParams.parent_id}` : hrefWithoutExtraFilters.href,
    data
  });
  const canonicalHref = new URL(mainQueryLength(queryParams) > 1 ? href.split('?')[0] : href);
  canonicalHref.searchParams.delete('page');
  canonicalHref.searchParams.delete('villa');
  let defaultCanonical = pageQuery && +pageQuery > 1 ? href : canonicalHref.href;
  let mainFilterExists = false;
  Object.keys(queryParams).filter(q => {
    return mainFilters?.includes(q);
  }).forEach(q => {
    if (queryParams[q]) {
      mainFilterExists = true;
    }
  });
  if (placeName) {
    defaultCanonical = `https://www.shab.ir/search/city/تهران?${queryParams.place_id ? 'place_id' : 'tag'}=${queryParams.place_id || queryParams.tag}`;
    breadcrumbs = placeFilters({
      placeName,
      queryParams
    });
  }
  if (queryParams.near_cities) {
    breadcrumbs = nearCitiesFilters({
      city: queryParams.near_cities
    });
  }
  if (village) {
    breadcrumbs = villageFilters({
      village,
      data
    });
  }
  const canonical = () => {
    let canonical = isWhiteLabel ? host : 'https://www.shab.ir';
    const existedMainFilters = mainFilters.filter(item => !!queryParams?.[item]);
    if (!city && !province && !mainFilterExists && !queryParams.near_cities && !village) {
      if (!!queryParams.warm_water_pool) {
        canonical = `https://www.shab.ir?pool=1`;
      } else {
        canonical = 'https://www.shab.ir';
      }
    } else {
      if (!!queryParams.near_cities) {
        canonical += `/search?near_cities=${queryParams.near_cities}`;
      } else {
        if (existedMainFilters.length === 0) {
          if (!!queryParams.warm_water_pool) {
            canonical += `${pathname}?pool=1`;
          } else {
            canonical += pathname;
          }
        } else if (existedMainFilters.length === 1) {
          if (!!queryParams.warm_water_pool && !existedMainFilters.includes('pool')) {
            canonical += pathname;
          } else {
            canonical += `${pathname}?${existedMainFilters[0]}=1`;
          }
        } else {
          canonical += pathname;
        }
      }
      if (!!queryParams.page && (+queryParams.page || 1) > 1) {
        if (canonical.includes('?')) {
          canonical += `&page=${queryParams.page}`;
        } else {
          canonical += `?page=${queryParams.page}`;
        }
      }
    }

    // Only the unscoped boomgardi and cottage searches map to their landings.
    // City, province, and village pages keep their own filtered href.
    const isUnscopedLanding = existedMainFilters.length === 1 && !city && !province && !village;
    const isUnscopedCottage = isUnscopedLanding && existedMainFilters[0] === 'cottage' && canonical.includes('cottage=1') && !canonical.includes('swiss-cottage=1');
    const isUnscopedBoomgardi = isUnscopedLanding && canonical.includes('boomgardi=1');
    if (isUnscopedCottage || isUnscopedBoomgardi) {
      const result = new URL('https://www.shab.ir');
      result.pathname = isUnscopedCottage ? '/all-cottage' : '/all-boomgardi';
      if (!!queryParams.page) {
        result.searchParams.set('page', queryParams.page.toString());
      }
      return result.href;
    }
    return canonical;
  };
  const faqSchema = useMemo(() => {
    if (!data?.region_data?.faqs) return '';
    return `{"@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity":[${data?.region_data?.faqs.map(item => `
      {
        "@type": "Question",
        "name": "${item.question}",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "${item.answer}"
        }
      }
      `)}]
    }`;
  }, [data?.region_data?.faqs]);
  return <Head data-sentry-element="Head" data-sentry-component="SearchSeo" data-sentry-source-file="SearchSeo.tsx">
      <title>{title}</title>
      {!isWhiteLabel && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(breadcrumbs)
    }} />}
      {(tags.includes(params?.tag) || !!queryParams.mid_term && params.city !== 'تهران') && <meta name="robots" content="noindex, nofollow" />}
      {pathname === '/search' && Object.keys(queryParams).length === 0 && <meta name="robots" content="noindex, follow" />}
      <meta name="description" content={description} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta property="og:title" content={title} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta property="og:description" content={description} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta property="og:url" content={canonical()} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta property="og:site_name" content={isWhiteLabel ? '' : 'سایت شب'} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta property="og:type" content="website" data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta property="og:image" content={image} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta name="twitter:card" content="summary_large_image" data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta name="twitter:site" content="@shabhost" data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta name="twitter:title" content={title} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta name="twitter:description" content={description} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      <meta name="twitter:image:src" content={image} data-sentry-element="meta" data-sentry-source-file="SearchSeo.tsx" />
      {nextExists && <link rel="next" href={urlWithoutPageNext.href} />}
      {prevPage > 0 && +pageQuery <= mainTotal + 1 && <link rel="prev" href={urlWithoutPagePrev.href} />}
      {!isWhiteLabel && <link rel="canonical" href={queryParams.parent_id ? `https://www.shab.ir/search?parent_id=${queryParams.parent_id}` : canonical()} />}

      {!isWhiteLabel ? <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: faqSchema
    }}></script> : null}
    </Head>;
};
export default SearchSeo;